<template>
  <Dialog v-model:open="open">
    <DialogTrigger v-if="$slots.trigger" as-child>
      <slot name="trigger" />
    </DialogTrigger>
    <DialogContent @pointer-down-outside.prevent @interact-outside.prevent>
      <template v-if="step === 'step1'">
        <DialogHeader>
          <DialogTitle class="!font-normal">Upgrade workspace</DialogTitle>
        </DialogHeader>
        <div class="px-7 pb-6 pt-5 max-w-[780px]">
          <slot name="upgradeMessage" />
          <StepOne @go-to-checkout="goToCheckout" @on-downgrade="downgrade" />
        </div>
      </template>
      <template v-if="step === 'step2'">
        <DialogHeader>
          <div class="flex gap-4 -ml-2">
            <IconButton
              variant="ghost"
              class="border-none p-1 size-8"
              @click="goBack()"
            >
              <ArrowLeft class="w-6 h-6" />
            </IconButton>
            <DialogTitle class="!font-normal">
              <template v-if="!currentSubscriptionPlan">
                Upgrade to {{ selectedProduct.title }} plan
              </template>
              <template v-else>Change plan</template>
            </DialogTitle>
          </div>
        </DialogHeader>
        <ChangePlanForm
          v-if="open && step === 'step2' && currentSubscriptionPlan"
          :selected-product="selectedProduct"
          @success-payment="step = 'step3'"
        />
        <template v-else>
          <Form
            v-if="open && step === 'step2'"
            :selected-product="selectedProduct"
            @success-payment="step = 'step3'"
          />
        </template>
      </template>
      <template v-if="step === 'step3'">
        <StepThree @modal-closed="close" />
      </template>
    </DialogContent>
  </Dialog>
  <DowngradeModal v-if="downgradeOpen" v-model:open="downgradeOpen" />
</template>

<script setup>
import { ref, watch, nextTick, computed, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { ArrowLeft } from 'lucide-vue-next';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
} from '@ui/components/dialog';

import { IconButton } from '@ui/components/icon-button';
import StepOne from './components/StepOne.vue';
import StepThree from './components/StepThree.vue';
import Form from './components/Form.vue';
import ChangePlanForm from './components/ChangePlanForm.vue';
import DowngradeModal from './components/DowngradeModal.vue';

const store = useStore();

const currentSubscriptionPlan = computed(
  () => store.getters['workspace/getCurrentSubscriptionPlan']
);

const plans = computed(() => store.getters['application/getSubscriptionPlans']);

const open = defineModel('open', { type: Boolean, default: false });

const downgradeOpen = ref(false);
const step = ref('step1');
const selectedProduct = ref(null);

watch(open, async next => {
  if (next) {
    await nextTick();
    step.value = 'step1';
  }
});

const goToCheckout = productId => {
  step.value = 'step2';
  selectedProduct.value = plans.value.find(
    plan => plan.product_id === productId
  );
};

const goBack = () => {
  step.value = 'step1';
};

const downgrade = async () => {
  open.value = false;
  await nextTick();
  downgradeOpen.value = true;
};

const close = () => {
  open.value = false;
  step.value = 'step1';
};

onUnmounted(() => {
  open.value = false;
  step.value = 'step1';
});
</script>
